import { MatterSubItemContextMenu } from "@/components/features/matter/context-menus/MatterSubItemContextMenu";
import { useUser } from "@/components/UserProvider";
import type { TrustCheckViewModel } from "@/hooks/matter-view-models/use-trust-checks-view-model";
import { useDeleteMatterTrustCheck } from "@/lib/data/react-query/matters/matter-trust-check";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import type { ReactNode } from "react";

interface MatterTrustCheckContextMenuProps {
  matter: Matter;
  onEdit: () => void;
  trigger: ReactNode;
  trustCheck: TrustCheckViewModel;
}

export const MatterTrustCheckContextMenu = ({
  matter,
  onEdit,
  trigger,
  trustCheck,
}: MatterTrustCheckContextMenuProps) => {
  const { hasActionPermission } = useUser();
  const deleteTrustCheck = useDeleteMatterTrustCheck();
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTrustCheck,
    ACTION_PERMISSION_VALUES.delete,
  );

  return (
    <MatterSubItemContextMenu
      copyValue={[
        trustCheck.type,
        `Amount: $${trustCheck.amount ?? 0}`,
        trustCheck.referenceNumber
          ? `Reference #: ${trustCheck.referenceNumber}`
          : undefined,
        trustCheck.dateReceived
          ? `Date: ${formatDate(trustCheck.dateReceived)}`
          : undefined,
      ]
        .filter(Boolean)
        .join("\n")}
      delete={
        canDelete
          ? {
              disabled: deleteTrustCheck.isPending,
              onSelect: async () => {
                await deleteTrustCheck.mutateAsync({ id: trustCheck.id });
              },
            }
          : false
      }
      entityLabel={`${trustCheck.type.toLowerCase()} trust check`}
      entityName={`${trustCheck.type.toLowerCase()} trust check`}
      matter={matter}
      onEdit={onEdit}
      subItem={{ id: trustCheck.id, type: "trust-check" }}
      trigger={trigger}
    />
  );
};
